import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { URL_IMAGES } from '../service/endpoints';
import { deleteData, getData, patchData, postData } from '../service/APIClient';

export const fetchImages = createAsyncThunk('images/fetchImages', async () => {
  let images = [];
  let currentPage = 1;
  let totalPages = 1;

  try {
    while (currentPage <= totalPages) {
      const response = await getData(`${URL_IMAGES}?limit=1000?page=${currentPage}`);
      images = [...images, ...response.data];
      totalPages = response.totalPages;
      currentPage++;
    }
    return images.map(image => ({
      key: image._id.toString(),
      filename: image.filename,
      blobId: image.blobId,
      url: image.url,
      fileType: image.fileType,
      dimensions: image.dimensions,
      tags: image.tags,
      type: image.type,
      tenantId: image.tenantId,
      createdAt: new Date(image.createdAt).toISOString().split('T')[0],
      updatedAt: new Date(image.updatedAt).toISOString().split('T')[0],
    }));
  } catch (error) {
    throw new Error('Failed to fetch images');
  }
});

export const addImageAsync = createAsyncThunk(
  'images/addImage',
  async (formData, thunkAPI) => {
    try {
      const createdImage = await postData(URL_IMAGES, formData, {
        'Content-Type': 'multipart/form-data',
      });
      return {
        key: createdImage._id.toString(),
        filename: createdImage.filename,
        blobId: createdImage.blobId,
        url: createdImage.url,
        fileType: createdImage.fileType,
        dimensions: createdImage.dimensions,
        tags: createdImage.tags,
        type: createdImage.type,
        tenantId: createdImage.tenantId,
        createdAt: new Date(createdImage.createdAt).toISOString().split('T')[0],
        updatedAt: new Date(createdImage.updatedAt).toISOString().split('T')[0],
      };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to add image';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const deleteImageAsync = createAsyncThunk(
  'images/deleteImage',
  async (imageId, thunkAPI) => {
    try {
      await deleteData(`${URL_IMAGES}/blob/${imageId}`);
      return imageId;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to delete image';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const updateImageAsync = createAsyncThunk(
  'images/updateImage',
  async ({ imageId, updatedData }, thunkAPI) => {
    try {
      await patchData(`${URL_IMAGES}/${imageId}`, updatedData);
      return { imageId, updatedData };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update image';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

const imagesSlice = createSlice({
  name: 'images',
  initialState: {
    data: [],
    loading: false,
    addStatus: 'idle',
    updateStatus: 'idle',
    deleteStatus: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchImages.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImages.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addImageAsync.pending, state => {
        state.addStatus = 'loading';
        state.error = null;
      })
      .addCase(addImageAsync.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        state.data.push(action.payload);
        // fetchImages()
      })
      .addCase(addImageAsync.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteImageAsync.pending, state => {
        state.deleteStatus = 'loading';
        state.error = null;
      })
      .addCase(deleteImageAsync.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.data = state.data.filter(image => image.key !== action.payload);
        // fetchImages()
      })
      .addCase(deleteImageAsync.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(updateImageAsync.pending, state => {
        state.updateStatus = 'loading';
        state.error = null;
      })
      .addCase(updateImageAsync.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        const { imageId, updatedData } = action.payload;
        const index = state.data.findIndex(image => image.key === imageId);
        if (index !== -1) {
          state.data[index] = {
            ...state.data[index],
            ...updatedData, // Merge updated fields into existing object
          };
        }
      })
      .addCase(updateImageAsync.rejected, (state, action) => {
        state.updateStatus = 'failed';
        state.error = action.payload || action.error.message;
      });
  },
});

export default imagesSlice.reducer;
